import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useAssessment } from '../../../../contexts/AssessmentContext'
import { calculateAssessmentProgress } from '../../../../shared/utils/clientHelpers'
import { calculateScores } from '../../../../shared/utils/scoring'
import SelectedAssessmentActions from '../../../../shared/components/SelectedAssessmentActions'

function AssessmentsCard({ client }) {
  const { deleteAssessment } = useAssessment()
  const [deleteConfirmId, setDeleteConfirmId] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [selectedId, setSelectedId] = useState(null)

  const activeAssessments = useMemo(() =>
    (client.assessments || []).filter(a => a.status === 'in_progress'),
    [client.assessments]
  )
  const completedAssessments = useMemo(() =>
    (client.assessments || [])
      .filter(a => a.status === 'completed')
      .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt)),
    [client.assessments]
  )

  const allAssessments = [...activeAssessments, ...completedAssessments]

  // Auto-clear selection if the selected assessment was deleted
  const selectionStillValid = selectedId && allAssessments.some(a => a.id === selectedId)
  if (selectedId && !selectionStillValid) {
    // defer so we don't setState during render
    setTimeout(() => setSelectedId(null), 0)
  }

  return (
    <>
      {selectedId && (
        <SelectedAssessmentActions
          assessmentId={selectedId}
          client={client}
          onClose={() => setSelectedId(null)}
        />
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700/30 pb-3 mb-4">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Assessments
            <span className="ml-2 font-normal">({(client.assessments || []).length})</span>
          </h3>
          <Link to={`/clients/${client.id}/new-assessment`} className="btn-primary text-sm">
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {completedAssessments.length > 0 ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              )}
            </svg>
            {completedAssessments.length > 0 ? 'Reassess' : 'New Assessment'}
          </Link>
        </div>

        {allAssessments.length === 0 ? (
          <div className="text-center py-8">
            <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p className="text-gray-500 dark:text-gray-400 mb-3">No assessments yet</p>
            <p className="text-sm text-gray-400 dark:text-gray-500">Click "New Assessment" to get started</p>
          </div>
        ) : (
          <div className="space-y-3" role="radiogroup" aria-label="Assessment selection">
            {allAssessments.map(assessment => {
              const isCompleted = assessment.status === 'completed'
              const progress = !isCompleted ? calculateAssessmentProgress(assessment) : null
              const isActive = !isCompleted
              const isSelected = selectedId === assessment.id

              const toggle = () => setSelectedId(prev => prev === assessment.id ? null : assessment.id)
              const onKey = (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  toggle()
                }
              }

              return (
                <div
                  key={assessment.id}
                  role="radio"
                  aria-checked={isSelected}
                  tabIndex={0}
                  onClick={toggle}
                  onKeyDown={onKey}
                  className={`relative block p-4 rounded-lg transition-colors cursor-pointer group ${
                    isSelected
                      ? 'bg-primary-50 dark:bg-primary-900/30 border-2 border-primary-500 dark:border-primary-400'
                      : isActive
                      ? 'bg-gray-50 dark:bg-gray-700/50 border-2 border-transparent border-l-[3px] border-l-amber-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                      : 'bg-gray-50 dark:bg-gray-700/50 border-2 border-transparent hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2 gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <span
                        aria-hidden
                        className={`flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          isSelected
                            ? 'bg-primary-600 border-primary-600'
                            : 'border-gray-300 dark:border-gray-500 group-hover:border-primary-400'
                        }`}
                      >
                        {isSelected && (
                          <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 12 12">
                            <circle cx="6" cy="6" r="3" />
                          </svg>
                        )}
                      </span>
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 dark:text-white truncate">
                          {assessment.title || `Assessment - ${new Date(assessment.createdAt).toLocaleDateString()}`}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {isCompleted
                            ? `Completed ${new Date(assessment.completedAt).toLocaleDateString()}`
                            : `Started ${new Date(assessment.createdAt).toLocaleDateString()}`
                          }
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3 flex-shrink-0">
                      <div className="flex flex-col items-center gap-1">
                        <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                          isCompleted
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                            : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                        }`}>
                          {isCompleted ? 'Completed' : 'In Progress'}
                        </span>
                      </div>
                      {(() => {
                        const flagCount = Object.keys(assessment.responses?.flags || assessment.responses?.notes?.flags || {}).length
                        if (flagCount === 0) return null
                        return (
                          <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2z" />
                            </svg>
                            {flagCount}
                          </span>
                        )
                      })()}

                      {isCompleted && (() => {
                        let score = assessment.scores?.overall?.score
                        let level = assessment.scores?.overall?.maturityLevel?.label || 'Score'
                        try {
                          if (assessment.responses?.maturityAssessment) {
                            const computed = calculateScores(assessment.responses)
                            score = computed.overall?.score ?? score
                            level = computed.overall?.maturityLevel?.label ?? level
                          }
                        } catch { /* use cached */ }
                        return score !== undefined ? (
                          <div className="text-right">
                            <div className="text-lg font-bold text-primary-600 dark:text-accent-300">{score}%</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{level}</div>
                          </div>
                        ) : null
                      })()}

                      {!isCompleted && progress && (
                        <div className="text-right">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {progress.totalAnswered} of {progress.totalQuestions}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">questions answered</div>
                        </div>
                      )}

                      {isActive && (
                        <button
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            setDeleteConfirmId(assessment.id)
                          }}
                          className="opacity-0 group-hover:opacity-100 p-1.5 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                          title="Delete assessment"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>

                  {!isCompleted && progress && (
                    <div className="flex items-center space-x-3 mt-2">
                      <div className="flex-grow h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary-500 dark:bg-accent-300 rounded-full transition-all duration-300"
                          style={{ width: `${progress.percent}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-300 w-12 text-right">
                        {progress.percent}%
                      </span>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
        {allAssessments.length > 0 && !selectedId && (
          <p className="mt-3 text-xs text-gray-400 dark:text-gray-500 italic">
            Click an assessment to reveal Generate Report and Download Data actions.
          </p>
        )}
      </div>

      {/* Delete confirmation modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setDeleteConfirmId(null)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-sm mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">Delete Assessment</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">This action cannot be undone.</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
              All responses and progress for this in-progress assessment will be permanently removed.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirmId(null)}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setDeleting(true)
                  try {
                    await deleteAssessment(deleteConfirmId)
                    if (selectedId === deleteConfirmId) setSelectedId(null)
                  } catch (err) {
                    console.error('Failed to delete assessment:', err)
                  } finally {
                    setDeleting(false)
                    setDeleteConfirmId(null)
                  }
                }}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default AssessmentsCard
